// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * cm-coding: Administrators use this dashboard to compare monthly job health
 * by organization or workspace and then inspect recent failures. Scope changes
 * only the identifiers sent to the same summary and recent-job queries; keep
 * one content structure for both levels so switching scope cannot change the
 * meaning or placement of operational status.
 */
import { useState, useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { format, subMonths } from 'date-fns'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui'
import { SegmentedTabs } from '@/components/ui'
import { MonthlyStatsView } from './MonthlyStatsView'
import { RecentJobsTable } from './RecentJobsTable'
import { AuthErrorAlert } from "./AuthErrorAlert"

type JobScope = 'application' | 'organization' | 'workspace'

interface JobsComponentProps {
  org_id?: string;
  ws_id?: string;
  isGlobalAdmin?: boolean;
  metadata_fields?: Record<string, string>;
}

export function JobsComponent({
  org_id,
  ws_id,
  isGlobalAdmin = false,
  metadata_fields
}: JobsComponentProps) {
  const [month, setMonth] = useState(format(new Date(), 'yyyy-MM'))

  // Memoized available tabs based on permissions
  const availableTabs = useMemo(() => {
    const tabs: JobScope[] = []
    if (isGlobalAdmin) {
      tabs.push('application')
      if (org_id) tabs.push('organization')
    }
    if (org_id && ws_id) tabs.push('workspace')
    return tabs
  }, [isGlobalAdmin, org_id, ws_id])

  // Memoized month options for dropdown
  const monthOptions = useMemo(() =>
    Array.from({ length: 6 }, (_, i) => {
      const date = subMonths(new Date(), i)
      return { value: format(date, 'yyyy-MM'), label: format(date, 'MMMM yyyy') }
    })
  , [])

  // Fallback is never rendered — an empty tab list short-circuits below
  const defaultTab: JobScope = availableTabs.includes('workspace') ? 'workspace' :
                     availableTabs.includes('organization') ? 'organization' : 'application'

  // URL-synced tab state with internal state for immediate UI response
  const [searchParams, setSearchParams] = useSearchParams()
  const urlTab = searchParams.get('tab') as JobScope | null
  const [internalTab, setInternalTab] = useState(urlTab && availableTabs.includes(urlTab) ? urlTab : defaultTab)
  const activeTab = urlTab && availableTabs.includes(urlTab) ? urlTab : internalTab

  const handleTabChange = useCallback((tabId: JobScope) => {
    setInternalTab(tabId)
    setSearchParams(prev => {
      const next = new URLSearchParams(prev)
      next.set('tab', tabId)
      return next
    }, { replace: true })
  }, [setSearchParams])

  // Org/workspace context narrows with the selected scope
  const contextOrgId = activeTab === 'application' ? undefined : org_id
  const contextWsId = activeTab === 'workspace' ? ws_id : undefined

  const scopeTitle = activeTab === 'application' ? 'Application Jobs'
    : activeTab === 'organization' ? 'Organization Jobs'
    : 'Workspace Jobs'
  const scopePath = activeTab === 'application' ? null
    : activeTab === 'organization' ? org_id
    : `${org_id}/${ws_id}`

  if (availableTabs.length === 0) {
    return (
      <div className="py-8 text-center text-[13px] text-slate-500">
        <p>No jobs available to display. {!isGlobalAdmin && 'Admin access required to view application-level jobs.'}</p>
        {!org_id && <p className="mt-2">Please provide organization and workspace IDs to view workspace jobs.</p>}
      </div>
    )
  }

  return (
    <div className="text-slate-900">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[21px] font-[650] tracking-[-0.015em]">Jobs</h1>
          <p className="mt-[5px] text-[13px] text-slate-500">Monitor processing jobs and statistics</p>
        </div>
        <div className="flex items-center gap-2.5">
          <SegmentedTabs
            tabs={availableTabs.map(id => ({
              id,
              label: id.charAt(0).toUpperCase() + id.slice(1),
            }))}
            value={activeTab}
            onChange={handleTabChange}
          />
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger
              aria-label="Jobs month"
              className="h-auto gap-2 rounded-[9px] border-slate-200 bg-white px-3.5 py-[7px] text-[12.5px] font-[550] text-slate-900 shadow-[0_1px_2px_rgba(2,8,23,0.04)]"
            >
              <SelectValue/>
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="mt-7 mb-3.5 flex items-center gap-2.5">
        <span className="text-sm font-semibold tracking-[-0.01em]">{scopeTitle}</span>
        {scopePath && (
          <span className="rounded-md border border-slate-200 bg-slate-100 px-2 py-[3px] font-mono text-[11.5px] text-slate-600 break-all">
            {scopePath}
          </span>
        )}
      </div>

      <AuthErrorAlert />

      <MonthlyStatsView
        month={month}
        org_id={contextOrgId}
        ws_id={contextWsId}
      />

      <RecentJobsTable
        month={month}
        org_id={contextOrgId}
        ws_id={contextWsId}
        metadata_fields={metadata_fields}
      />
    </div>
  )
}
